import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchVerificationEvents } from '../api/endpoints';
import type { VerificationEvent } from '../api/types';
import { useApiData } from '../hooks/useApiData';
import { formatClockTime, formatDate, formatIsoDate } from '../utils/format';
import { ErrorState, LoadingState } from './LoadingState';

const CATEGORIES: Array<[string, string]> = [
  ['session', 'Session'],
  ['step', 'Customer steps'],
  ['ocr', 'OCR'],
  ['decision', 'Checks and decision'],
  ['fraud', 'Fraud alerts'],
  ['webhook', 'Webhooks'],
  ['api', 'API calls'],
];

function Marker({ tone }: { tone: string }) {
  switch (tone) {
    case 'success':
      return <i className="bi bi-check-circle event-icon text-success" aria-label="Passed" />;
    case 'danger':
      return <i className="bi bi-x-circle event-icon text-danger" aria-label="Failed" />;
    case 'warning':
      return <i className="bi bi-exclamation-triangle event-icon text-warning" aria-label="Warning" />;
    default:
      return <span className="event-dot" />;
  }
}

/**
 * A verification's events, newest first: created, the customer's steps, checks, the decision,
 * reviews, fraud alerts, webhooks. Built by VerificationEvents (Core). FEATURES.md §7. React twin
 * of web/Pages/Shared/_EventTimelinePartial.cshtml.
 */
export function EventTimelineCard({ verificationId }: { verificationId: number }) {
  const { data, loading, error } = useApiData(() => fetchVerificationEvents(verificationId), [verificationId]);
  const [category, setCategory] = useState('');

  const events: VerificationEvent[] = data ?? [];
  const present = new Set(events.map((e) => e.category));
  const shown = events.filter((e) => !category || e.category === category);

  return (
    <div className="card event-timeline-card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-semibold">
          <i className="bi bi-clock-history me-2" />
          Event timeline (UTC)
        </h6>
        <Link to={`/api-logs?verificationId=${verificationId}`} className="small">
          Open in API Logs
        </Link>
      </div>
      <div className="card-body">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} />
        ) : events.length === 0 ? (
          <p className="text-body-secondary small mb-0">No events recorded for this verification.</p>
        ) : (
          <>
            <select
              className="form-select form-select-sm mb-3 event-timeline-filter"
              aria-label="Event type"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All event types</option>
              {CATEGORIES.filter(([value]) => present.has(value)).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <ol className="event-timeline">
              {shown.map((e, i) => {
                const day = formatIsoDate(e.at);
                const newDay = i === 0 || formatIsoDate(shown[i - 1].at) !== day;
                return (
                  <Fragment key={`${e.at}-${i}`}>
                    {newDay && (
                      <li className="event-timeline-date">
                        <span>{formatDate(e.at)}</span>
                        <span className="event-marker" />
                        <span />
                      </li>
                    )}
                    <li className="event-timeline-item">
                      <span className="event-time">{formatClockTime(e.at)}</span>
                      <span className="event-marker">
                        <Marker tone={e.tone} />
                      </span>
                      <span className="event-body">
                        <span className="event-title">{e.title}</span>
                        {e.detail &&
                          (e.logId != null ? (
                            <Link to={`/api-logs/${e.logId}`} className="event-detail">
                              {e.detail}
                            </Link>
                          ) : (
                            <span className="event-detail">{e.detail}</span>
                          ))}
                      </span>
                    </li>
                  </Fragment>
                );
              })}
            </ol>
            <p className="text-body-secondary small mt-3 mb-0">
              Customer steps, API calls and webhooks come from the API log, kept for 90 days (since 19 Oct 2026).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
